import { Users, Clock, DollarSign, TrendingDown } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { useWorkplaceCurrency } from '@/contexts/WorkplaceCurrencyContext';
import type { PayrollPreviewTotals } from '@/services/payroll/payrollPreviewService';

interface PreviewSummaryCardsProps {
  totals: PayrollPreviewTotals;
}

export const PreviewSummaryCards = ({ totals }: PreviewSummaryCardsProps) => {
  const { currencyCode } = useWorkplaceCurrency();
  
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
    }).format(amount);
  };

  const formatHours = (hours: number) => {
    return hours.toFixed(2);
  };

  return (
    <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
      {/* Total Employees - 1 col on lg */}
      <Card className="lg:col-span-1">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-primary/10 rounded-lg flex-shrink-0">
              <Users className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Employees</p>
              <p className="text-2xl font-bold">{totals.total_employees}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Total Hours - 1 col on lg */}
      <Card className="lg:col-span-1">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-primary/10 rounded-lg flex-shrink-0">
              <Clock className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Hours</p>
              <p className="text-2xl font-bold">{formatHours(totals.total_hours)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Total Gross Pay - 2 cols on lg */}
      <Card className="lg:col-span-2">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-primary/10 rounded-lg flex-shrink-0">
              <DollarSign className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Gross Pay</p>
              <p className="text-2xl font-bold">
                {formatCurrency(totals.total_gross)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Total Net Pay - 2 cols on lg */}
      <Card className="lg:col-span-2">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-green-500/10 rounded-lg flex-shrink-0">
              <TrendingDown className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Net Pay</p>
              <p className="text-2xl font-bold text-green-600">
                {formatCurrency(totals.total_net)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
